import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, Loader2, Upload, X, HardHat, FileCheck2, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SERVICES = [
  ["Request Plans & Permit Assistance", "svc.permit", FileCheck2],
  ["Multi-Trade Contractor Services", "svc.contractor", HardHat],
  ["Renovation & Repairs Contractor", "svc.renovation", Hammer],
];

const buildContextSummary = (report, context) => {
  const r = report || context || {};
  const parts = [];
  if (r.site_address || r.address) parts.push(`Address: ${r.site_address || r.address}`);
  if (r.folio) parts.push(`Folio: ${r.folio}`);
  if (r.jurisdiction) parts.push(`Jurisdiction: ${r.jurisdiction}`);
  if (r.zoning) parts.push(`Zoning: ${r.zoning}`);
  if (Array.isArray(r.permits)) parts.push(`Permits: ${r.permits.length}`);
  if (Array.isArray(r.violations)) parts.push(`Violations: ${r.violations.length}`);
  return parts.join(" · ");
};

export const AssistanceDialog = ({ open, onOpenChange, report, context, defaultService, extra }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [service, setService] = useState(defaultService || "");
  const [form, setForm] = useState({
    name: user?.first_name || "", email: user?.email || "", phone: user?.phone || "",
    preferred: "either", description: "",
  });
  const [images, setImages] = useState([]);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const r = report || context || {};

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, { path: data.path, name: f.name }]);
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!form.name || !form.email) return toast.error(t("assist.nameEmail"));
    if (!consent) return toast.error(t("pro.consentReq"));
    setBusy(true);
    try {
      await axios.post(`${API}/permit-assistance`, {
        name: form.name, email: form.email, phone: form.phone,
        preferred_contact: form.preferred,
        request_type: service || "professional",
        description: `${service ? `[Service] ${service}\n` : ""}${form.description}\n\n[Context] ${buildContextSummary(report, context)}${extra ? `\n[Details] ${extra}` : ""}`,
        folio: r.folio, address: r.site_address || r.address,
        images: images.map((i) => i.path),
        context: r,
        consent: true,
      });
      toast.success(t("ask.submitted"));
      onOpenChange(false);
      setForm({ ...form, description: "" }); setImages([]); setConsent(false);
    } catch { toast.error(t("assist.submitErr")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="assistance-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("pro.title")}</DialogTitle>
          <DialogDescription>{t("pro.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{t("pro.service")}</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {SERVICES.map(([id, key, Icon]) => (
                <button key={id} data-testid="service-chip" onClick={() => setService(service === id ? "" : id)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${service === id ? "border-accent bg-accent text-accent-foreground" : "border-border bg-secondary/50 text-foreground/80 hover:border-primary/50"}`}>
                  <Icon className="h-3.5 w-3.5" />{t(key)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div><Label className="text-xs">{t("auth.firstName")}</Label><Input data-testid="pro-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label className="text-xs">{t("pro.email")}</Label><Input data-testid="pro-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label className="text-xs">{t("pro.phone")}</Label><Input data-testid="pro-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label className="text-xs">{t("pro.preferred")}</Label>
              <select data-testid="pro-preferred" value={form.preferred} onChange={(e) => setForm({ ...form, preferred: e.target.value })}
                className="mt-0.5 h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                <option value="email">{t("pro.email")}</option>
                <option value="phone">{t("pro.phone")}</option>
                <option value="either">{t("pro.either")}</option>
              </select>
            </div>
          </div>
          <div><Label className="text-xs">{t("assist.sit")}</Label><Textarea data-testid="pro-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

          <div>
            <Label className="text-xs">{t("pro.attach")}</Label>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="pro-file-input" onChange={onFiles} />
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" className="gap-1.5" data-testid="pro-attach-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {t("pro.attach")}
              </Button>
              {images.map((img, i) => (
                <span key={i} data-testid="pro-image-chip" className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-[11px]">
                  {img.name.slice(0, 18)}
                  <button onClick={() => setImages(images.filter((_, x) => x !== i))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>

          {r.folio && <p className="rounded-md bg-secondary/50 p-2 font-mono text-[10px] text-muted-foreground">{buildContextSummary(report, context)}</p>}
          <label className="flex cursor-pointer items-start gap-2 text-xs text-muted-foreground" data-testid="pro-consent-label">
            <input type="checkbox" data-testid="pro-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
            <span>{t("pro.consent")}</span>
          </label>
          <Button className="w-full gap-2" data-testid="pro-submit" onClick={submit} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("pro.btn")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ProfessionalAssistance = ({ report, context, defaultService }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="pro-assistance" className="rounded-xl border border-accent/40 bg-accent/5 p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground"><Wrench className="h-5 w-5" /></div>
        <div className="flex-1">
          <p className="font-serif text-lg font-semibold text-foreground">{t("pro.title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("pro.desc")}</p>
          <Button className="mt-3" data-testid="request-pro-btn" onClick={() => setOpen(true)}>{t("pro.btn")}</Button>
        </div>
      </div>
      <AssistanceDialog open={open} onOpenChange={setOpen} report={report} context={context} defaultService={defaultService} />
    </div>
  );
};

export const ServicesBanner = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [svc, setSvc] = useState("");
  return (
    <div data-testid="services-banner" className="mx-auto mt-10 max-w-5xl px-4 sm:px-6">
      <div className="overflow-hidden rounded-2xl border border-border bg-primary text-primary-foreground">
        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-serif text-xl font-semibold">{t("banner.title")}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SERVICES.map(([id, key, Icon]) => (
              <button key={id} data-testid="banner-service" onClick={() => { setSvc(id); setOpen(true); }}
                className="flex items-center gap-1.5 rounded-full bg-primary-foreground/10 px-2.5 py-1.5 text-[11px] font-medium ring-1 ring-primary-foreground/20 transition-colors hover:bg-primary-foreground/20">
                <Icon className="h-3.5 w-3.5" />{t(key)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <AssistanceDialog open={open} onOpenChange={setOpen} defaultService={svc} />
    </div>
  );
};
